import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import axios from 'axios'
import { IconClose } from './icons'

const SYNC_OPTIONS = [
  {
    id: 'aspire-properties',
    label: 'Aspire Properties',
    desc: 'Refresh property records and matching data.',
    method: 'get',
    path: '/api/aspire/properties/sync',
    params: { autoUpdate: true }
  },
  {
    id: 'aspire-contacts',
    label: 'Aspire Contacts',
    desc: 'Refresh Aspire contacts.',
    method: 'get',
    path: '/api/aspire/contacts/sync'
  },
  {
    id: 'aspire-companies',
    label: 'Aspire Companies',
    desc: 'Refresh Aspire companies.',
    method: 'get',
    path: '/api/aspire/companies/sync'
  },
  {
    id: 'pipedrive',
    label: 'PipeDrive',
    desc: 'No PipeDrive sync endpoint is registered in this codebase yet.',
    disabled: true
  }
]

function DashboardModal({ children, onClose, wide }) {
  return (
    <div className="dashboard-modal-backdrop" onMouseDown={onClose}>
      <div className={`dashboard-modal${wide ? ' dashboard-modal-wide' : ''}`} onMouseDown={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  )
}

function SyncModal({ url, onClose }) {
  const [running, setRunning] = useState(null)
  const [results, setResults] = useState({})

  const runSync = async (option) => {
    if (option.disabled || !url) return
    setRunning(option.id)
    try {
      const res = await axios({
        method: option.method,
        url: `${url}${option.path}`,
        params: option.params
      })
      setResults(prev => ({
        ...prev,
        [option.id]: {
          ok: true,
          message: res.data?.message || res.data?.status || 'Sync started'
        }
      }))
    } catch (err) {
      setResults(prev => ({
        ...prev,
        [option.id]: {
          ok: false,
          message: err.response?.data?.message || err.message || 'Sync failed'
        }
      }))
    } finally {
      setRunning(null)
    }
  }

  return (
    <DashboardModal onClose={onClose}>
      <div className="dashboard-modal-head">
        <div>
          <h3>Sync Now</h3>
          <span>Choose the system data to refresh.</span>
        </div>
        <button className="rail-icon-btn" onClick={onClose} title="Close"><IconClose size={16} /></button>
      </div>
      <div className="sync-option-list">
        {SYNC_OPTIONS.map(option => {
          const result = results[option.id]
          return (
            <button
              key={option.id}
              className="sync-option"
              disabled={option.disabled || running === option.id}
              onClick={() => runSync(option)}
            >
              <span>
                <b>{option.label}</b>
                <small>{option.desc}</small>
                {result && <small className={result.ok ? 'sync-ok' : 'sync-error'}>{result.message}</small>}
              </span>
              <em>{running === option.id ? 'Running...' : option.disabled ? 'Unavailable' : 'Run'}</em>
            </button>
          )
        })}
      </div>
      <div className="dashboard-modal-actions">
        <button className="dbtn dbtn-outline" onClick={onClose}>Close</button>
      </div>
    </DashboardModal>
  )
}

function ChangeLogModal({ items, onClose }) {
  const recent = (items || []).slice(0, 12)
  return (
    <DashboardModal onClose={onClose} wide>
      <div className="dashboard-modal-head">
        <div>
          <h3>Dashboard Changelog</h3>
          <span>Recent bid, queue, and dashboard activity.</span>
        </div>
        <button className="rail-icon-btn" onClick={onClose} title="Close"><IconClose size={16} /></button>
      </div>
      <div className="changelog-list">
        {recent.length === 0 && <div className="dashboard-empty-state">No recent activity loaded.</div>}
        {recent.map((item, index) => (
          <a key={item._id || index} href={item.linkTo || '#'} onClick={item.linkTo ? undefined : e => e.preventDefault()}>
            <b>{item.label || 'Activity'}</b>
            <span>{item.desc || item.actor || 'No details'}</span>
            <em>{item.ts ? new Date(item.ts).toLocaleString() : ''}</em>
          </a>
        ))}
      </div>
    </DashboardModal>
  )
}

export default function PageHeader({ url, activityItems }) {
  const history = useHistory()
  const [syncOpen, setSyncOpen] = useState(false)
  const [changelogOpen, setChangelogOpen] = useState(false)

  return (
    <div className="page-head">
      <div className="page-title-row">
        <h1>Dashboard</h1>
        <span className="pill pill-ok"><span className="dot" />All systems synced</span>
        <span style={{ fontFamily: 'var(--f-mono)', fontSize: '11.5px', color: 'var(--ink-3)' }}>
          Last sync <b style={{ color: 'var(--ink-1)', fontWeight: 550 }}>2 min ago</b>
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="dbtn dbtn-ghost" onClick={() => setChangelogOpen(true)}>View changelog</button>
          <button className="dbtn dbtn-outline" onClick={() => setSyncOpen(true)}>Sync now</button>
          <button className="dbtn dbtn-primary" onClick={() => history.push('/bid-estimation?new=1')}>+ New project</button>
        </div>
      </div>
      {syncOpen && <SyncModal url={url} onClose={() => setSyncOpen(false)} />}
      {changelogOpen && <ChangeLogModal items={activityItems} onClose={() => setChangelogOpen(false)} />}
    </div>
  )
}
